import { ExecutionTask } from './types';
import { computeRetryDelayMs } from './managerUtils';
import { getRetryAfterSeconds, isQuotaExhausted, isRateLimitError } from '@/utils/httpErrors';
import { Runtime } from './runtime';
import { logRetryScheduled } from './logging';
import { actions as uiActions } from './state/uiTaskReducer';
import type { UiTaskAction } from './state/uiTaskReducer';

export type RetryPolicyDeps = {
	runtime: Runtime;
	updateExecutionTask: (taskId: string, executionUpdates: Partial<ExecutionTask>) => void;
	processQueuedTasks: () => void;
	dispatch: (action: UiTaskAction) => void;
};

/**
 * Creates a retry policy with scheduling and decision helpers.
 */
export function createRetryPolicy(deps: RetryPolicyDeps) {
	const { runtime, updateExecutionTask, processQueuedTasks, dispatch } = deps;

	/**
	 * Only rate limiting is worth retrying: no route the task manager drives returns a 409, and
	 * upload answers 202 with an `outcome` instead.
	 *
	 * Bounded by elapsed time rather than by attempts. Each wait is an appointment the server set, so
	 * ten of them are under a minute against a burst ceiling and over an hour against a daily
	 * allowance — a count would measure how busy the server is, not how long the client has waited.
	 * `retry_scope` decides only what the task settles as once the time is up; see
	 * `bundle/docs/platform/rate-limiting.md`.
	 */
	const shouldRetry = (executionTask: ExecutionTask, error: unknown): boolean => {
		if (!isRateLimitError(error)) return false;
		if (executionTask.retryingSince === undefined) return true;
		return Date.now() - executionTask.retryingSince < executionTask.maxRateLimitWaitMs;
	};

	const scheduleRetry = (executionTask: ExecutionTask, error?: unknown): void => {
		const updatedTask: ExecutionTask = {
			...executionTask,
			retryCount: executionTask.retryCount + 1,
			// Stamped on the first wait, so the bound measures how long this task has been held up
			// rather than restarting with every denial.
			retryingSince: executionTask.retryingSince ?? Date.now(),
		};

		// Prefer the server's Retry-After (rate limits); otherwise exponential backoff.
		const retryAfterSeconds = getRetryAfterSeconds(error);
		const delay = computeRetryDelayMs(executionTask.retryCount, retryAfterSeconds);

		// The API's own verdict on whether this clears in seconds or spent an allowance.
		const quotaExhausted = isQuotaExhausted(error);

		// Back to queued so it becomes eligible for re-dispatch. The bar is only wound back when the
		// wait is long enough to be worth showing: a burst token frees up in about five seconds, and
		// emptying a full bar and refilling it over that reads as a glitch rather than as throttling
		// the user never needed to know about. A spent allowance is minutes, where a bar left full
		// would be claiming progress that is not happening.
		dispatch(uiActions.updateStatus(executionTask.apiTask.id, 'queued', quotaExhausted ? 0 : undefined));
		dispatch(
			uiActions.updateMetadata(executionTask.apiTask.id, {
				retryCount: updatedTask.retryCount,
				// Recorded so the queue can say why a row stopped moving. Without it a task waiting out
				// a daily allowance is indistinguishable from one waiting for a free slot, and both
				// read "Uploading…" for the several minutes it takes.
				resumesAt: Date.now() + delay,
				quotaExhausted,
			}),
		);
		logRetryScheduled(
			executionTask.apiTask.id,
			updatedTask.retryCount,
			Date.now() - updatedTask.retryingSince!,
			delay,
		);

		setTimeout(() => {
			// Allow re-dispatching this task on the next processing pass
			runtime.releaseDispatched(executionTask.apiTask.id);

			// Update execution state with incremented retry count
			updateExecutionTask(executionTask.apiTask.id, updatedTask);

			// Re-run the processor to pick up queued tasks according to capacity
			processQueuedTasks();
		}, delay);
	};

	return { shouldRetry, scheduleRetry };
}
